import { useRef, useState, useCallback, useEffect } from "react";
import { useContentEditingSafe } from "./ContentEditingContext";
import { useAuthSafe } from "./AuthContext";
import { RotateCcw, Trash2, ChevronDown, Check, X, Type } from "lucide-react";
import { AiWriterButton } from "./AiWriterButton";
import { FieldHistoryButton } from "./FieldHistoryPopup";
import { recordFieldChange } from "../../utils/fieldHistoryApi";
import { handleFormatKeyDown, extractRichValue, hasHtmlFormatting } from "./InlineFormatToolbar";
import { TypographyPanel } from "./TypographyPanel";

const BLOCK_TAGS = new Set(["h1", "h2", "h3", "p", "div", "li"]);
const MULTILINE_TAGS = new Set(["p", "div", "li"]);

/** Human-readable label for element types */
const TAG_LABELS: Record<string, string> = {
  h1: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA",
  h2: "\u05DB\u05D5\u05EA\u05E8\u05EA",
  h3: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DE\u05E9\u05E0\u05D4",
  p: "\u05E4\u05E1\u05E7\u05D4",
  span: "\u05D8\u05E7\u05E1\u05D8",
  li: "\u05E4\u05E8\u05D9\u05D8",
  div: "\u05EA\u05D5\u05DB\u05DF",
  label: "\u05EA\u05D5\u05D5\u05D9\u05EA",
};

/** Tags available in the tag switcher picker */
const TAG_SWITCH_OPTIONS: { value: string; label: string }[] = [
  { value: "h1", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05E8\u05D0\u05E9\u05D9\u05EA" },
  { value: "h2", label: "\u05DB\u05D5\u05EA\u05E8\u05EA" },
  { value: "h3", label: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DE\u05E9\u05E0\u05D4" },
  { value: "p", label: "\u05E4\u05E1\u05E7\u05D4" },
  { value: "span", label: "\u05D8\u05E7\u05E1\u05D8" },
];

/** Badge color scheme per tag */
const TAG_BADGE_COLORS: Record<string, string> = {
  h1: "bg-purple-100 text-purple-700 hover:bg-purple-200",
  h2: "bg-blue-100 text-blue-700 hover:bg-blue-200",
  h3: "bg-cyan-100 text-cyan-700 hover:bg-cyan-200",
  p: "bg-gray-100 text-gray-600 hover:bg-gray-200",
  span: "bg-gray-50 text-gray-500 hover:bg-gray-100",
  li: "bg-gray-100 text-gray-500 hover:bg-gray-200",
  div: "bg-gray-100 text-gray-500 hover:bg-gray-200",
  label: "bg-gray-100 text-gray-500 hover:bg-gray-200",
};

/* ================================================================
   EditableText
   ================================================================ */

interface EditableTextProps {
  /** Unique key for this content piece, e.g. "kitchen-carpentry:hero:h1" */
  contentKey: string;
  /** Default/original value from code */
  defaultValue: string;
  /** HTML tag to render */
  as?: "h1" | "h2" | "h3" | "p" | "span" | "li" | "div" | "label";
  /** Additional className */
  className?: string;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** For multiline content (p, div) */
  multiline?: boolean;
  /** Hide the HTML tag badge and picker in edit mode */
  hideTagBadge?: boolean;
  /** Compact mode — reduces spacing */
  compact?: boolean;
}

export function EditableText({
  contentKey,
  defaultValue,
  as: OriginalTag = "span",
  className = "",
  style,
  multiline,
  hideTagBadge,
  compact,
}: EditableTextProps) {
  const ctx = useContentEditingSafe();
  const auth = useAuthSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const getContent = ctx?.getContent ?? ((key: string, def: string) => def);
  const saveContent = ctx?.saveContent ?? (() => {});
  const hasOverride = ctx?.hasOverride ?? (() => false);
  const resetContent = ctx?.resetContent ?? (() => {});
  const siteId = ctx?.currentSiteId ?? "default";
  const username = auth?.user?.displayName || auth?.user?.username;

  // Register this content key for the completion meter
  useEffect(() => {
    ctx?.registerContentKey(contentKey);
    return () => { ctx?.unregisterContentKey(contentKey); };
  }, [contentKey]);

  const elementRef = useRef<HTMLElement>(null);
  const typoBtnRef = useRef<HTMLSpanElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [showTypoPanel, setShowTypoPanel] = useState(false);

  // Ref to track if save/cancel buttons were clicked
  const actionClickedRef = useRef<"save" | "cancel" | null>(null);

  // ——— Tag override system ———
  const tagKey = `tag:${contentKey}`;
  const tagOverrideRaw = getContent(tagKey, "");
  const isTagOverridden = hasOverride(tagKey) && tagOverrideRaw !== "";
  const EffectiveTag = (isTagOverridden ? tagOverrideRaw : OriginalTag) as typeof OriginalTag;

  // Auto-enable multiline for block-level text tags
  const isMultiline = multiline !== undefined ? multiline : MULTILINE_TAGS.has(EffectiveTag);

  const displayValue = getContent(contentKey, defaultValue);
  const isOverridden = hasOverride(contentKey);

  // ——— Typography overrides ———
  const fontSizeKey = `fontSize:${contentKey}`;
  const colorKey = `color:${contentKey}`;
  const fontWeightKey = `fontWeight:${contentKey}`;

  const savedFontSize = getContent(fontSizeKey, "");
  const savedColor = getContent(colorKey, "");
  const savedFontWeight = getContent(fontWeightKey, "");

  const isFontSizeOverridden = hasOverride(fontSizeKey);
  const isColorOverridden = hasOverride(colorKey);
  const isFontWeightOverridden = hasOverride(fontWeightKey);
  const isTypoOverridden = isFontSizeOverridden || isColorOverridden || isFontWeightOverridden;

  const typographyStyle: React.CSSProperties = {
    ...(savedFontSize ? { fontSize: savedFontSize } : {}),
    ...(savedColor ? { color: savedColor } : {}),
    ...(savedFontWeight ? { fontWeight: Number(savedFontWeight) } : {}),
  };

  const handleFontSize = useCallback((v: string) => {
    if (v) saveContent(fontSizeKey, v); else resetContent(fontSizeKey);
  }, [fontSizeKey, saveContent, resetContent]);

  const handleColor = useCallback((v: string) => {
    if (v) saveContent(colorKey, v); else resetContent(colorKey);
  }, [colorKey, saveContent, resetContent]);

  const handleFontWeight = useCallback((v: string) => {
    if (v) saveContent(fontWeightKey, v); else resetContent(fontWeightKey);
  }, [fontWeightKey, saveContent, resetContent]);

  const handleTypoReset = useCallback(() => {
    resetContent(fontSizeKey);
    resetContent(colorKey);
    resetContent(fontWeightKey);
  }, [fontSizeKey, colorKey, fontWeightKey, resetContent]);

  // ——— Deleted state ———
  const deletedKey = `hidden:${contentKey}`;
  const isDeleted = getContent(deletedKey, "false") === "true";

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    saveContent(deletedKey, "true");
  }, [deletedKey, saveContent]);

  const handleRestore = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resetContent(deletedKey);
  }, [deletedKey, resetContent]);

  // ——— Core save logic ———
  const doSave = useCallback(() => {
    if (!elementRef.current) return;
    const newValue = extractRichValue(elementRef.current);
    if (newValue !== defaultValue) {
      if (displayValue && displayValue !== newValue) {
        recordFieldChange(siteId, contentKey, displayValue, newValue, username).catch(() => {});
      }
      saveContent(contentKey, newValue);
    } else {
      resetContent(contentKey);
    }
  }, [contentKey, defaultValue, displayValue, siteId, username, saveContent, resetContent]);

  // ——— Blur handler ———
  const handleBlur = useCallback(() => {
    requestAnimationFrame(() => {
      if (actionClickedRef.current === "cancel") {
        if (elementRef.current) elementRef.current.textContent = displayValue;
        actionClickedRef.current = null;
        setIsFocused(false);
        return;
      }
      if (actionClickedRef.current === "save") {
        doSave();
        actionClickedRef.current = null;
        setIsFocused(false);
        return;
      }
      doSave();
      setIsFocused(false);
    });
  }, [displayValue, doSave]);

  const handleManualSave = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    doSave();
    setIsFocused(false);
    elementRef.current?.blur();
  }, [doSave]);

  const handleManualCancel = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (elementRef.current) elementRef.current.textContent = displayValue;
    setIsFocused(false);
    elementRef.current?.blur();
  }, [displayValue]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Handle formatting shortcuts (Ctrl+B, Ctrl+U)
    if (handleFormatKeyDown(e)) return;

    if (e.key === "Enter") {
      if (e.shiftKey || isMultiline) {
        e.preventDefault();
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          range.deleteContents();
          const br = document.createElement("br");
          range.insertNode(br);
          const textNode = document.createTextNode("\u200B");
          range.setStartAfter(br);
          range.insertNode(textNode);
          range.setStartAfter(textNode);
          range.setEndAfter(textNode);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      } else {
        e.preventDefault();
        doSave();
        setIsFocused(false);
        elementRef.current?.blur();
      }
    }
    if (e.key === "Escape") {
      if (elementRef.current) elementRef.current.textContent = displayValue;
      setIsFocused(false);
      elementRef.current?.blur();
    }
  }, [isMultiline, displayValue, doSave]);

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    resetContent(contentKey);
    if (elementRef.current) elementRef.current.textContent = defaultValue;
  }, [contentKey, defaultValue, resetContent]);

  const handleAiAccept = useCallback((newContent: string) => {
    saveContent(contentKey, newContent);
    if (elementRef.current) elementRef.current.textContent = newContent;
  }, [contentKey, saveContent]);

  const handleTagChange = useCallback((newTag: string) => {
    if (newTag === OriginalTag) resetContent(tagKey);
    else saveContent(tagKey, newTag);
    setShowTagPicker(false);
  }, [OriginalTag, tagKey, saveContent, resetContent]);

  // Typography panel open handler
  const handleTypoOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setShowTypoPanel((prev) => !prev);
    setShowTagPicker(false);
  }, []);

  // Sync DOM content from React state ONLY when not focused
  useEffect(() => {
    if (isEditMode && elementRef.current && !isFocused) {
      if (hasHtmlFormatting(displayValue)) {
        elementRef.current.innerHTML = displayValue;
      } else if (displayValue.includes("\n")) {
        elementRef.current.innerHTML = displayValue.replace(/\n/g, "<br>");
      } else {
        elementRef.current.textContent = displayValue;
      }
    }
  }, [isEditMode, displayValue, isFocused, EffectiveTag]);

  // Close tag picker when focused
  useEffect(() => {
    if (isFocused) setShowTagPicker(false);
  }, [isFocused]);

  // Close typo panel when focused
  useEffect(() => {
    if (isFocused) setShowTypoPanel(false);
  }, [isFocused]);

  // Helper: render text with line breaks as React elements
  const renderWithLineBreaks = (text: string) => {
    if (!text.includes("\n")) return text;
    const parts = text.split("\n");
    return parts.map((part, i) => (
      <span key={i} style={{ whiteSpace: "pre-wrap" }}>
        {part}
        {i < parts.length - 1 && <br />}
      </span>
    ));
  };

  // Build tag picker options
  const pickerOptions = TAG_SWITCH_OPTIONS.some((o) => o.value === OriginalTag)
    ? TAG_SWITCH_OPTIONS
    : [{ value: OriginalTag, label: TAG_LABELS[OriginalTag] || OriginalTag }, ...TAG_SWITCH_OPTIONS];

  /* ——— Non-edit mode ——— */
  if (!isEditMode) {
    if (isDeleted) return null;
    if (hasHtmlFormatting(displayValue)) {
      return (
        <EffectiveTag
          className={className}
          style={{ ...style, ...typographyStyle }}
          data-content-key={contentKey}
          dangerouslySetInnerHTML={{ __html: displayValue }}
        />
      );
    }
    return (
      <EffectiveTag
        className={className}
        style={{ ...style, ...typographyStyle }}
        data-content-key={contentKey}
      >
        {renderWithLineBreaks(displayValue)}
      </EffectiveTag>
    );
  }

  /* ——— Edit mode — deleted ——— */
  if (isDeleted) return null;

  /* ——— Edit mode — visible ——— */
  const isBlock = BLOCK_TAGS.has(EffectiveTag);
  const Wrapper = isBlock ? "div" : "span";

  return (
    <Wrapper className={`relative ${showTagPicker || showTypoPanel ? "z-[50]" : "hover:z-[30]"} ${isBlock ? "block" : "inline-block"} group/editable ${hideTagBadge ? "" : "pt-5"}`}>
      <EffectiveTag
        ref={elementRef as any}
        className={`${className} outline-none ${
          isFocused
            ? "ring-2 ring-zap ring-offset-2 bg-zap-pale/50 rounded px-1"
            : "hover:ring-2 hover:ring-zap/40 hover:ring-offset-1 rounded cursor-text"
        }`}
        style={{
          ...style,
          ...typographyStyle,
          ...(isOverridden ? { borderBottom: "2px dashed #fbbf24" } : {}),
          transition: "transform 0.15s",
          minHeight: "1em",
        }}
        contentEditable
        suppressContentEditableWarning
        onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        dir="rtl"
        data-content-key={contentKey}
      />

      {/* ——— Save / Cancel buttons ——— */}
      {isFocused && (
        <span className="inline-flex items-center gap-1 mt-1.5" onClick={(e) => e.stopPropagation()}>
          <span
            role="button"
            tabIndex={0}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); actionClickedRef.current = "save"; }}
            onClick={handleManualSave}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleManualSave(e as any); } }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 shadow-sm cursor-pointer select-none transition-colors"
            style={{ fontWeight: 600 }}
            title="\u05E9\u05DE\u05D5\u05E8"
          >
            <Check className="w-3 h-3" />
            {"\u05E9\u05DE\u05D5\u05E8"}
          </span>
          <span
            role="button"
            tabIndex={0}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); actionClickedRef.current = "cancel"; }}
            onClick={handleManualCancel}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleManualCancel(e as any); } }}
            className="inline-flex items-center gap-1 px-2.5 py-1 text-xs bg-gray-100 text-gray-600 rounded-md hover:bg-gray-200 shadow-sm cursor-pointer select-none transition-colors"
            style={{ fontWeight: 600 }}
            title="\u05D1\u05D9\u05D8\u05D5\u05DC"
          >
            <X className="w-3 h-3" />
            {"\u05D1\u05D8\u05DC"}
          </span>
        </span>
      )}

      {/* ——— Tag badge (top-right, visible in edit mode when not focused) ——— */}
      {!isFocused && !hideTagBadge && (
        <span className="absolute top-0 right-0 z-[20]" onClick={(e) => e.stopPropagation()}>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShowTagPicker(!showTagPicker); }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setShowTagPicker(!showTagPicker); }
            }}
            className={`inline-flex items-center gap-0.5 text-xs leading-none px-2.5 py-1 rounded-full cursor-pointer select-none transition-all font-mono shadow-sm ${
              isTagOverridden
                ? "bg-amber-100 text-amber-700 ring-1 ring-amber-300 hover:bg-amber-200"
                : `${TAG_BADGE_COLORS[EffectiveTag] || "bg-gray-100 text-gray-500"} opacity-80 group-hover/editable:opacity-100`
            }`}
            style={{ fontWeight: 700 }}
            title={`\u05EA\u05D2\u05D9\u05EA HTML: ${EffectiveTag.toUpperCase()}${isTagOverridden ? ` (\u05DE\u05E7\u05D5\u05E8: ${OriginalTag.toUpperCase()})` : ""}`}
          >
            {EffectiveTag.toUpperCase()}
            <ChevronDown className="w-2 h-2" />
          </span>

          {/* Tag picker dropdown */}
          {showTagPicker && (
            <>
              <div className="fixed inset-0 z-[99]" onClick={() => setShowTagPicker(false)} />
              <div
                className="absolute top-full right-0 mt-1 z-[100] bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[170px]"
                dir="rtl"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 py-1.5 text-[10px] text-gray-400 border-b border-gray-100 select-none" style={{ fontWeight: 500 }}>
                  {"\u05E9\u05E0\u05D4 \u05EA\u05D2\u05D9\u05EA HTML"}
                </div>
                {pickerOptions.map((opt) => {
                  const isActive = opt.value === EffectiveTag;
                  const isOriginal = opt.value === OriginalTag;
                  return (
                    <div
                      key={opt.value}
                      onClick={(e) => { e.stopPropagation(); handleTagChange(opt.value); }}
                      className={`px-3 py-1.5 text-xs cursor-pointer flex items-center justify-between gap-2 transition-colors ${
                        isActive ? "bg-blue-50 text-blue-700" : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={`font-mono text-[10px] px-1.5 py-0.5 rounded ${
                            isActive ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-500"
                          }`}
                          style={{ fontWeight: 700 }}
                        >
                          {opt.value.toUpperCase()}
                        </span>
                        <span>{opt.label}</span>
                      </span>
                      <span className="flex items-center gap-1">
                        {isOriginal && !isActive && <span className="text-[9px] text-gray-400">({"\u05DE\u05E7\u05D5\u05E8"})</span>}
                        {isActive && <Check className="w-3.5 h-3.5 text-blue-500" />}
                      </span>
                    </div>
                  );
                })}
                {isTagOverridden && (
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <div
                      onClick={(e) => { e.stopPropagation(); handleTagChange(OriginalTag); }}
                      className="px-3 py-1.5 text-xs cursor-pointer text-amber-600 hover:bg-amber-50 flex items-center gap-2 transition-colors"
                    >
                      <RotateCcw className="w-3 h-3" />
                      <span>{"\u05D0\u05E4\u05E1 \u05DC\u05EA\u05D2\u05D9\u05EA \u05D4\u05DE\u05E7\u05D5\u05E8\u05D9\u05EA"} ({OriginalTag.toUpperCase()})</span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </span>
      )}

      {/* ——— Action buttons — appear on hover (top-left) ——— */}
      {!isFocused && (
        <span
          className={`absolute ${hideTagBadge ? "-top-5 left-0" : "top-0 -left-2"} z-10 flex items-center gap-0.5 opacity-0 group-hover/editable:opacity-100 transition-opacity`}
        >
          {/* Typography button */}
          <span
            ref={typoBtnRef}
            role="button"
            tabIndex={0}
            onClick={handleTypoOpen}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleTypoOpen(e as any); } }}
            className={`relative w-5 h-5 flex items-center justify-center rounded-full shadow-sm cursor-pointer select-none ${
              showTypoPanel
                ? "bg-purple-600 text-white ring-2 ring-purple-300"
                : isTypoOverridden
                ? "bg-purple-500 text-white hover:bg-purple-600"
                : "bg-gray-200 text-gray-600 hover:bg-purple-100 hover:text-purple-700"
            }`}
            title={"\u05E2\u05D9\u05E6\u05D5\u05D1 \u05D8\u05E7\u05E1\u05D8 \u2014 \u05D2\u05D5\u05D3\u05DC, \u05E6\u05D1\u05E2, \u05E2\u05D5\u05D1\u05D9"}
          >
            <Type className="w-2.5 h-2.5" />
            {/* Indicator dot when typography is overridden */}
            {isTypoOverridden && !showTypoPanel && (
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-yellow-400 rounded-full border border-white" />
            )}
          </span>

          {/* AI Writer button */}
          <AiWriterButton
            contentKey={contentKey}
            currentContent={displayValue}
            elementType={EffectiveTag}
            onAccept={handleAiAccept}
            size="sm"
          />

          {/* Delete button */}
          <span
            role="button"
            tabIndex={0}
            onClick={handleDelete}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleDelete(e as any); } }}
            className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 shadow-sm cursor-pointer select-none"
            title="\u05DE\u05D7\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8"
          >
            <Trash2 className="w-3 h-3" />
          </span>

          {/* Reset content button (only when content overridden) */}
          {isOverridden && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleReset}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleReset(e as any); } }}
              className="w-5 h-5 bg-amber-400 text-white rounded-full flex items-center justify-center hover:bg-amber-500 shadow-sm cursor-pointer select-none"
              title="\u05D0\u05E4\u05E1 \u05DC\u05E2\u05E8\u05DA \u05D4\u05DE\u05E7\u05D5\u05E8\u05D9"
            >
              <RotateCcw className="w-3 h-3" />
            </span>
          )}

          {/* Field History button */}
          <FieldHistoryButton
            siteId={siteId}
            contentKey={contentKey}
            currentValue={displayValue}
            onRestore={(value) => {
              saveContent(contentKey, value);
              if (elementRef.current) elementRef.current.textContent = value;
            }}
          />
        </span>
      )}

      {/* ——— Typography Panel (portal) ——— */}
      {showTypoPanel && (
        <TypographyPanel
          savedFontSize={savedFontSize}
          savedColor={savedColor}
          savedFontWeight={savedFontWeight}
          onFontSize={handleFontSize}
          onColor={handleColor}
          onWeight={handleFontWeight}
          onReset={handleTypoReset}
          onClose={() => setShowTypoPanel(false)}
          anchorEl={typoBtnRef.current}
          editableEl={elementRef.current}
          showBullets
        />
      )}

    </Wrapper>
  );
}